import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { NavManager } from '../nav-manager';
import { select, Store } from '@ngrx/store';
import { AnEnvProvider, ANServiceEndPoints } from '../analytic-env-provider';
import { TUserLoggedInInfo, UnifiedLoginService, CapacitorHttpService } from '@monorepo/services';
import { IAppState, selectHeaderInfo } from '../../state';
import { Util } from '../../common-components/util';
import { InitializeFactory } from '../../state/factory/factory.actions';
import { TRESPONSE_Factory_Api, TREQUEST_Factory_Api, THeaderResponse, TNavigation } from '@monorepo/types';
import { NavController } from '@ionic/angular';
import { Observable } from 'rxjs';
import { HeaderFacade } from '../../state/header/header.facade';
import { LOCAL_STORAGE_UTILS } from '@monorepo/utils';
import { AnalyticsConfigCacheService } from '../analytics-config-cache.service';

@Injectable({
  providedIn: 'root',
})
export class ResolverService {
  private config_role = 'MULTI_CONFIGS';
  #selectedHeaderInfo$: Observable<THeaderResponse>;
  #headers!: THeaderResponse['navigationTabs'];
  constructor(
    private envProvider: AnEnvProvider,
    private unifiedLoginService: UnifiedLoginService,
    private navMgr: NavManager,
    private store: Store<IAppState>,
    private capacitorHttpService: CapacitorHttpService,
    public navController: NavController,
    private headerFacade: HeaderFacade,
    private analyticsConfigCache: AnalyticsConfigCacheService,
  ) {
    this.#selectedHeaderInfo$ = this.store.pipe(select(selectHeaderInfo));
    this.#selectedHeaderInfo$.subscribe((hInfo) => {
      this.#headers = hInfo.navigationTabs;
    });
  }
  #isFactoryConfigrationLoaded = (navigationTabs: THeaderResponse['navigationTabs']) => {
    const isAnalyticsPresent = navigationTabs.includes('ANALYTICS');
    const isMmAnalyticsPresent = navigationTabs.includes('MM_ANALYTICS');
    const isSkilli8AnalyticsPresent = navigationTabs.includes('SKILLI8_ANALYTICS');

    const isSingleAnalyticsPresent =
      (isAnalyticsPresent && !isMmAnalyticsPresent && !isSkilli8AnalyticsPresent) ||
      (!isAnalyticsPresent && isMmAnalyticsPresent && !isSkilli8AnalyticsPresent) ||
      (!isAnalyticsPresent && !isMmAnalyticsPresent && isSkilli8AnalyticsPresent);

    return !!this.navMgr.getInspireTree() && isSingleAnalyticsPresent;
  };

  resolve(route: ActivatedRouteSnapshot) {
    const isMmAnalyticsRoute = route.routeConfig?.path === 'page-mm-analytics';
    const isSkilli8AnalyticsRoute = route.routeConfig?.path === 'page-skilli8-analytics';
    this.navMgr.ismMAnalytics = isMmAnalyticsRoute;
    this.navMgr.isSkilli8Analytics = isSkilli8AnalyticsRoute;

    const redirectedTab = this.#handleAnalyticsRouting(isMmAnalyticsRoute, isSkilli8AnalyticsRoute);

    if (redirectedTab) {
      this.navMgr.clearNavigation();
      return Promise.resolve(); // Empty promise to avoid loading the factory info when redirected
    }

    if (this.#isFactoryConfigrationLoaded(this.#headers)) {
      return Promise.resolve();
    }

    return this.#loadAnalyticsConfiguration(isMmAnalyticsRoute, isSkilli8AnalyticsRoute);
  }

  async #loadAnalyticsConfiguration(isMmAnalytics: boolean, isSkilli8Analytics: boolean = false) {
    const cachedConfig = await this.analyticsConfigCache.getCachedConfig(isMmAnalytics, isSkilli8Analytics);

    if (cachedConfig) {
      this.#applyFactoryState(cachedConfig);
      return cachedConfig;
    }

    const config = await (isSkilli8Analytics
      ? this.#fetchSkilli8AnalyticsConfig()
      : isMmAnalytics
      ? this.#fetchMmAnalyticsConfig()
      : this.#fetchOiConfig());

    this.#applyFactoryState(config);
    await this.analyticsConfigCache.cacheConfig(isMmAnalytics, config, isSkilli8Analytics);

    return config;
  }

  #applyFactoryState(factoryState: TRESPONSE_Factory_Api['result'][0]) {
    this.navMgr.setupNavManager(factoryState);
    this.store.dispatch(new InitializeFactory(factoryState));
    this.headerFacade.headerLoaded();
  }

  async #fetchOiConfig() {
    const url = this.envProvider.getEnvVar(ANServiceEndPoints.DS_IN_DG);

    const { roles, factoryId, legacyJWT } = this.unifiedLoginService.getUserInfo() as TUserLoggedInInfo;
    const locale = LOCAL_STORAGE_UTILS.retrieveData('locale');
    const request: TREQUEST_Factory_Api = {
      query_name: 'get_oi_app_config_v2',
      subject_key: factoryId,
      ...(roles.indexOf(this.config_role) > -1 && {
        query_name: 'get_oi_app_multi_config_v3',
      }),
      ...(locale ? { language_code: locale } : {}),
    };

    const httpOptions = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${legacyJWT}`,
    };

    const response = await this.capacitorHttpService.postPromise<TRESPONSE_Factory_Api, 'COMPRESSED'>(
      url,
      request,
      httpOptions,
    );

    return Util.decode<TRESPONSE_Factory_Api>(response)['result'][0];
  }

  async #fetchMmAnalyticsConfig() {
    const url = this.envProvider.getEnvVar(ANServiceEndPoints.MM_ANALYTICS);
    const userInfo = this.unifiedLoginService.getUserInfo() as TUserLoggedInInfo;
    // [NOTE]: This check can be removed after authentication service changes are done
    // and backend sends filtered list for `assignedFactoryIds` based on user role
    const isGroupUser = userInfo.roles.includes('OI_GROUP_MM_USER');
    const factoryIds: string[] =
      userInfo.assignedFactoryIds?.length && isGroupUser ? userInfo.assignedFactoryIds : [userInfo.factoryId];

    const locale = LOCAL_STORAGE_UTILS.retrieveData('locale');
    const httpOptions = { 'Content-Type': 'application/json', Authorization: `Bearer ${userInfo.legacyJWT}` };

    const request: Omit<TREQUEST_Factory_Api, 'subject_key'> = {
      query_name: 'get_oi_app_config_v2',
      ...(userInfo.roles.indexOf(this.config_role) > -1 && {
        query_name: 'get_oi_app_multi_config_v3',
      }),
      ...(locale ? { language_code: locale } : {}),
    };

    const responses = await Promise.all(
      factoryIds.map((id) => {
        return this.capacitorHttpService.postPromise<TRESPONSE_Factory_Api, 'UNCOMPRESSED'>(
          url,
          { ...request, subject_key: id },
          httpOptions,
        );
      }),
    );
    const factoryStates = responses.map((res) => res['result'][0]);

    const combinedFactoryState = factoryStates.reduce((acc, factoryState, idx) => {
      if (!acc) return factoryState;
      const prefix = factoryIds[idx] + '_';

      const addPrefix = (node: TNavigation) => {
        if (node.args['view_id']) node.args['view_id'] = prefix + node.args['view_id'];
        if (node['itree']) node['itree'] = undefined;
        if (!node.args['sk']) node.args['sk'] = factoryIds[idx];

        node.children?.forEach(addPrefix);
      };

      // make navigation factory specific
      factoryState?.meta_data.navigation.forEach(addPrefix);
      acc.meta_data.navigation.push(...(factoryState?.meta_data.navigation ?? []));

      // make components factory specific
      acc.meta_data.components.push(
        ...(factoryState?.meta_data.components ?? []).map((component) => ({
          ...component,
          id: prefix + component.id,
        })),
      );

      // make views factory specific
      factoryState?.meta_data.views?.forEach((view) => {
        if (view.view_id) view.view_id = (prefix + view.view_id) as any;
        Object.values(view.view_spec ?? {}).forEach((value) => {
          if (Array.isArray(value)) {
            for (let i = 0; i < value.length; i++) {
              value[i] = prefix + value[i];
            }
          }
        });
      });
      acc.meta_data.views.push(...(factoryState?.meta_data.views ?? []));

      return acc;
    }, undefined);

    return combinedFactoryState;
  }

  async #fetchSkilli8AnalyticsConfig() {
    const url = this.envProvider.getEnvVar(ANServiceEndPoints.SKILLI8_ANALYTICS);

    const { roles, factoryId, legacyJWT } = this.unifiedLoginService.getUserInfo() as TUserLoggedInInfo;
    const locale = LOCAL_STORAGE_UTILS.retrieveData('locale');
    const httpOptions = { 'Content-Type': 'application/json', Authorization: `Bearer ${legacyJWT}` };

    const request: Omit<TREQUEST_Factory_Api, 'subject_key'> = {
      query_name: 'get_oi_app_config_v2',
      ...(roles.indexOf(this.config_role) > -1 && {
        query_name: 'get_oi_app_multi_config_v3',
      }),
      ...(locale ? { language_code: locale } : {}),
    };

    const response = await this.capacitorHttpService.postPromise<TRESPONSE_Factory_Api, 'UNCOMPRESSED'>(
      url,
      { ...request, subject_key: factoryId },
      httpOptions,
    );

    return response['result'][0];
  }

  #handleAnalyticsRouting(
    isMmAnalyticsRoute: boolean,
    isSkilli8AnalyticsRoute: boolean = false,
  ): THeaderResponse['navigationTabs'][number] | false {
    // If navigating to skilli8 analytics route and tab exists, allow it
    if (isSkilli8AnalyticsRoute && this.#headers.includes('SKILLI8_ANALYTICS')) {
      return false;
    }

    if (
      !isMmAnalyticsRoute &&
      !isSkilli8AnalyticsRoute &&
      !this.#headers.includes('ANALYTICS') &&
      this.#headers.includes('MM_ANALYTICS')
    ) {
      this.navController.navigateRoot('/page-mm-analytics/redirect');
      return 'MM_ANALYTICS';
    }

    if (isMmAnalyticsRoute && !this.#headers.includes('MM_ANALYTICS') && this.#headers.includes('ANALYTICS')) {
      this.navController.navigateRoot('/page-analytics/redirect');
      return 'ANALYTICS';
    }

    if (this.#headers.includes('ANALYTICS') || this.#headers.includes('MM_ANALYTICS')) {
      return false;
    }

    if (!this.#headers.includes('ANALYTICS') && !this.#headers.includes('MM_ANALYTICS')) {
      if (this.#headers.includes('SKILLI8_ANALYTICS')) {
        this.navController.navigateRoot('/page-skilli8-analytics/redirect');
        return 'SKILLI8_ANALYTICS';
      } else if (this.#headers.includes('REPORTS_V2')) {
        this.navController.navigateRoot('reports-v2');
        return 'REPORTS_V2';
      } else if (this.#headers.includes('REPORTS')) {
        this.navController.navigateRoot('page-reports');
        return 'REPORTS';
      }
    }
    return false;
  }
}
